import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { Route } from '@treams/treams-routes';

import * as graphql from 'generated/graphql';
import Column from 'packages/kakapo-ui-kit/components/grid/Column';
import { JourneyItem } from 'packages/kakapo-ui-kit/components/journey/JourneyListItem';

import { PageTitle } from '../../../kakapo-ui-kit/components/common/Headings';
import JourneyList from '../../../kakapo-ui-kit/components/journey/JourneyList';
import LearningEventModalID from '../learning/pages/LearningEventModal';

interface IProps {
	user: graphql.User;
}

const FullPageBlueBackground = styled(Box)`
	&:before {
		position: fixed;
		z-index: -1;
		inset: 0;
		content: '';
		display: block;
		background-color: ${(props) => props.theme.colors.lightBlueLight};
	}
`;

export function JourneyListContainerWithoutBackground(props: { user: graphql.User }) {
	const { t } = useTranslation();
	const [items, setItems] = useState<JourneyItem[]>();

	const { user } = props;

	const {
		data: goalsData,
		loading: isLoadingGoals,
		error: goalsError,
	} = graphql.useIndividualGoalsQuery({
		variables: { id: user.id, status: graphql.GoalStatus.Active, last: 10 },
	});
	const goals = goalsData?.user.goalsV2.edges.map(({ node }) => node);

	const {
		data: archivedGoalsData,
		loading: isLoadingArchivedGoals,
		error: archivedGoalsError,
	} = graphql.useIndividualGoalsQuery({
		variables: { id: user.id, status: graphql.GoalStatus.Archived, last: 10 },
	});
	const archivedGoals = archivedGoalsData?.user.goalsV2.edges.map(({ node }) => node);

	const {
		data: completedGoalsData,
		loading: isLoadingCompletedGoals,
		error: completedGoalsError,
	} = graphql.useIndividualGoalsQuery({
		variables: { id: user.id, status: graphql.GoalStatus.Completed, last: 10 },
	});
	const completedGoals = completedGoalsData?.user.goalsV2.edges.map(({ node }) => node);

	// eslint-disable-next-line @typescript-eslint/naming-convention
	const [now] = useState(new Date().toString());

	const attendedLearningEvents = graphql.useAttendedLearningEventsForJourneyQuery({
		variables: {
			now,
			userID: user.id,
		},
	});

	const {
		data: evaluationsV2,
		loading: isLoadingEvaluationsV2,
		error: evaluationsV2Error,
	} = graphql.useEvaluationSubmissionsForJourneyQuery({
		variables: {
			userID: user.id,
		},
	});

	const feedbackV2 = graphql.useFeedbackRequestsQuery({
		variables: {
			userID: user.id,
			isArchived: false,
			last: 10,
		},
	});

	useEffect(() => {
		const itemArray: JourneyItem[] = [];

		setItems(
			itemArray
				.concat(goals ? (goals as graphql.Goal[]) : [])
				.concat(archivedGoals ? (archivedGoals as graphql.Goal[]) : [])
				.concat(completedGoals ? (completedGoals as graphql.Goal[]) : [])
				.concat(evaluationsV2?.user?.evaluationSubmissions?.edges.map((e) => e.node) ?? [])
				.concat(evaluationsV2?.user?.archivedEvaluationSubmissions?.edges.map((e) => e.node) ?? [])
				.concat(
					feedbackV2?.data?.user.feedbackRequests.edges.map(
						(e) => e.node as graphql.FeedbackRequest,
					) ?? [],
				)
				.concat(
					attendedLearningEvents.data?.user.attendingLearningEvents.edges.map(
						(edge) => edge.node,
					) ?? [],
				),
		);
	}, [goals, archivedGoals, evaluationsV2, feedbackV2.loading, attendedLearningEvents]);

	const isLoading =
		isLoadingGoals ||
		isLoadingCompletedGoals ||
		isLoadingArchivedGoals ||
		isLoadingEvaluationsV2 ||
		attendedLearningEvents.loading;
	const error =
		goalsError ??
		completedGoalsError ??
		archivedGoalsError ??
		evaluationsV2Error ??
		attendedLearningEvents.error ??
		null;

	return (
		<>
			<Route<{ id: string }> path="/user/:userId/journey/learning/:id">
				{({ match }) =>
					match && (
						<LearningEventModalID
							eventID={match.params.id!}
							returnURL={`/user/${user.id}/journey`}
						/>
					)
				}
			</Route>

			<Flex justifyContent="center" flexDirection="row" flexWrap="wrap-reverse">
				<Column width={[12 / 12, 12 / 12, 10 / 12]} spaced>
					<PageTitle>{t('journey.list.title', { firstName: user.firstName })}</PageTitle>

					<JourneyList
						isViewer={user.isViewer}
						items={items ?? []}
						isLoading={isLoading}
						error={error ?? null}
					/>
				</Column>
			</Flex>
		</>
	);
}

const JourneyListContainer = ({ user }: IProps) => {
	return (
		<FullPageBlueBackground my={-5} py={5}>
			<JourneyListContainerWithoutBackground user={user} />
		</FullPageBlueBackground>
	);
};

export default JourneyListContainer;
